import React, { useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FormLabel from "./FormLabel";
import { useTranslation } from "react-i18next";
import InputError from "./InputError";
import clsx from "clsx";
import { generateId, parseTypedClosedDate } from "../../../Utils";
import { CalendarIcon } from "@heroicons/react/outline";

const FormDate = ({
  label,
  id: propId,
  onChange,
  required = false,
  dateFormat = "dd/MM/yyyy",
  hint,
  selected,
  error,
  value,
  onClear,
  isDateClosed,
  ...props
}) => {
  const { t } = useTranslation();

  const id = propId || generateId();

  const hasError = Boolean(
    error?.message || error?.messages?.length || error?.length,
  );

  const inputClass = clsx(
    "block w-full rounded-md shadow-sm text-sm text-gray-900 placeholder:text-gray-400 border-0 ring-1 focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 pl-10",
    hasError
      ? "ring-red-300 focus:ring-red-500"
      : "ring-gray-300 focus:ring-blue-500",
  );

  const showClear = Boolean(selected || value) && onClear;

  // react-datepicker force-reopens the panel after every time edit
  // (handleTimeChange ends with `showTimeInput && setOpen(true)`), so the
  // date+time variant can never close itself once the clock has been touched.
  // Left open it covers the field below, and the next click lands on a day cell
  // instead, silently rewriting the date. Only this variant takes `open` over;
  // plain date fields keep the library's own close-on-select, which works.
  const withTime = Boolean(props.showTimeInput);
  const [open, setOpen] = useState(false);

  // The surrounding SlideOver closes on any Escape that reaches the window, and
  // a controlled `open` stops react-datepicker from marking the event handled
  // on our behalf. Contain it here so Escape only dismisses the calendar.
  const handleKeyDown = (event) => {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  // A closed-period date typed in is handed on rather than silently dropped,
  // so the form names the closed period. See parseTypedClosedDate.
  const handleChangeRaw = (event) => {
    props.onChangeRaw?.(event);
    const typed = parseTypedClosedDate(
      event?.target?.value,
      dateFormat,
      isDateClosed,
    );
    if (typed) onChange?.(typed, event);
  };

  const timeProps = withTime
    ? {
        open,
        // A day click no longer closes the panel: the clock still has to be
        // set. "Selesai" below is what ends the interaction.
        shouldCloseOnSelect: false,
        onInputClick: () => setOpen(true),
        onFocus: (event) => {
          props.onFocus?.(event);
          setOpen(true);
        },
        onClickOutside: () => setOpen(false),
        onKeyDown: (event) => {
          props.onKeyDown?.(event);
          handleKeyDown(event);
        },
        children: (
          // Rendered by react-datepicker inside .react-datepicker__children-container,
          // directly under the time field.
          <div className="px-3 pb-2 pt-1 text-right" onKeyDown={handleKeyDown}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              {t("common.done")}
            </button>
          </div>
        ),
      }
    : {};

  return (
    <div>
      {Boolean(label) && (
        <FormLabel htmlFor={id} required={required} hint={hint}>
          {t(label)}
        </FormLabel>
      )}
      <div className="relative">
        <ReactDatePicker
          // The label points at this id; without it the date field has no
          // accessible name.
          id={id}
          className={inputClass}
          selected={selected || value}
          onChange={onChange}
          dateFormat={dateFormat}
          // react-datepicker hard-codes these in English; the month and day
          // names around them follow the app language (see i18n.js).
          chooseDayAriaLabelPrefix={t("common.datePicker.choose")}
          disabledDayAriaLabelPrefix={t("common.datePicker.notAvailable")}
          previousMonthAriaLabel={t("common.datePicker.previousMonth")}
          nextMonthAriaLabel={t("common.datePicker.nextMonth")}
          previousYearAriaLabel={t("common.datePicker.previousYear")}
          nextYearAriaLabel={t("common.datePicker.nextYear")}
          {...props}
          onChangeRaw={handleChangeRaw}
          {...timeProps}
        />
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <CalendarIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
        </div>
        {showClear && (
          <div className={"absolute inset-y-0 right-0 pr-2 flex items-center"}>
            <button
              type="button"
              onClick={onClear}
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:text-gray-700 data-[disabled]:opacity-50"
            >
              <span className="sr-only">{t("common.clear")}</span>
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      {hasError && <InputError id={id} error={error} />}
    </div>
  );
};

export default FormDate;
